import React, { useState, useEffect, useRef } from 'react'
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"
import { addAdminMessage, markMessagesAsRead, subscribeToChat } from '../../firebase/supportChats'

const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

export default function DialogSupportChat({
    open,
    onClose,
    chat,
    currentUser,
    onUpdate
}) {
    const [message, setMessage] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [currentChat, setCurrentChat] = useState(chat)
    const messagesEndRef = useRef(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    useEffect(() => {
        if (open && chat) {
            console.log('Opening chat dialog for chat ID:', chat.id)
            console.log('Chat messages count:', chat.messages?.length)
            setMessage('')
            setIsLoading(false)
            setCurrentChat(chat)
            // Mark messages as read when opening chat
            markMessagesAsRead(chat.id)
            onUpdate()
        }
    }, [open, chat])

    // Real-time listener for the current chat
    useEffect(() => {
        let unsubscribe = null

        if (open && chat?.id) {
            console.log('Setting up real-time listener for chat:', chat.id)
            unsubscribe = subscribeToChat(chat.id, (updatedChat) => {
                console.log('Real-time update received for chat:', updatedChat.id)
                console.log('New messages count:', updatedChat.messages?.length)
                console.log('Current chat ID in listener:', chat.id)
                console.log('Updated chat ID:', updatedChat.id)
                setCurrentChat(updatedChat)
            })
        }

        return () => {
            if (unsubscribe) {
                console.log('Cleaning up real-time listener for chat:', chat?.id)
                unsubscribe()
            }
        }
    }, [open, chat?.id])

    useEffect(() => {
        scrollToBottom()
    }, [currentChat?.messages])

    const handleSendMessage = async () => {
        console.log('handleSendMessage called')
        console.log('Current message:', message)
        console.log('Current user:', currentUser)
        console.log('Current chat:', currentChat)
        
        if (!message.trim() || !currentUser) {
            console.log('Message or user is empty:', { message: message.trim(), currentUser })
            return
        }

        console.log('Sending message:', { chatId: currentChat.id, message: message.trim(), adminId: currentUser.uid, adminName: currentUser.email })
        
        setIsLoading(true)
        try {
                    console.log('About to send message to Firebase')
        console.log('Sending to chat ID:', currentChat.id)
        console.log('Current chat messages count:', currentChat.messages?.length)
        await addAdminMessage(
            currentChat.id, 
            message.trim(), 
            currentUser.uid, 
            currentUser.email || 'Admin'
        )
            setMessage('')
            console.log('Message sent successfully to Firebase')
            // Force a re-render by updating currentChat
            setTimeout(() => {
                console.log('Checking if real-time update came through...')
            }, 1000)
        } catch (error) {
            console.error('Error sending message:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleKeyPress = (e) => {
        console.log('Key pressed:', e.key)
        if (e.key === 'Enter' && !e.shiftKey) {
            console.log('Enter key pressed, calling handleSendMessage')
            e.preventDefault()
            handleSendMessage()
        }
    }

    return (
        <Dialog open={open} onClose={onClose} className="relative z-[10002]">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-[#000] backdrop-blur-sm bg-opacity-75 transition-opacity"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden bg-[#181818] rounded-[14px] backdrop-blur-sm shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in w-full max-w-[800px] h-[600px] flex flex-col"
                    >
                        <div className="w-full relative">
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 cursor-pointer z-[1]"
                            >
                                <img
                                    src="/assets/icons/icon-close.svg"
                                    alt="Close"
                                    className="invert cursor-pointer"
                                />
                            </button>
                            
                            {/* Header */}
                            <div className="p-6 border-b border-[#2F2F2F]">
                                <div className="flex items-center">
                                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                                        {currentChat?.userName?.charAt(0)?.toUpperCase() || 'U'}
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-white font-semibold text-lg">{currentChat?.userName}</h3>
                                        <p className="text-gray-400 text-sm">{currentChat?.userEmail}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-4">
                                    {currentChat?.messages && currentChat.messages.length > 0 ? (
                                        currentChat.messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={`flex ${msg.senderType === 'admin' ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={`max-w-[70%] ${msg.senderType === 'admin' ? 'bg-primary text-white' : 'bg-[#2F2F2F] text-white'} rounded-lg p-3`}>
                                                    <div className="text-sm mb-1">
                                                        <span className="font-medium">{msg.senderName}</span>
                                                        <span className="text-xs opacity-70 ml-2">
                                                            {formatDate(msg.timestamp)}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm">{msg.message}</div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-gray-400 py-8">
                                            <p>No messages yet</p>
                                            <p className="text-sm mt-2">Start the conversation by sending a message</p>
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            </div>

                            {/* Message Input */}
                            <div className="p-6 border-t border-[#2F2F2F]">
                                <div className="flex gap-3">
                                    <textarea
                                        className="flex-1 rounded-[10px] pl-3 py-3 bg-[#272727] text-white resize-none"
                                        placeholder="Type your message..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        rows={2}
                                        disabled={isLoading}
                                    />
                                    <button
                                        onClick={() => {
                                            console.log('Send button clicked')
                                            console.log('Button disabled:', !message.trim() || isLoading)
                                            handleSendMessage()
                                        }}
                                        disabled={!message.trim() || isLoading}
                                        className={`px-6 py-3 rounded-[10px] flex items-center ${
                                            message.trim() && !isLoading 
                                                ? 'bg-primary text-white' 
                                                : 'bg-[#E5E5E5] text-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {isLoading ? (
                                            <>
                                                <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-4 h-4 animate-spin mr-2" />
                                                Sending...
                                            </>
                                        ) : (
                                            'Send'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
} 